/**
 * @file <bk-review-form>: inline validation for the honest review form (snippets/review-form.liquid).
 *
 * The browser's own required/email rules stay the source of truth (so the form still validates with
 * JavaScript off); this element only replaces the native bubbles with messages tied to each control
 * through aria-invalid and aria-describedby, and moves focus to the first control that needs work.
 * Every message is read from a data-* attribute rendered with `| t`: no UI string lives here.
 * It also preselects the area the visitor already chose elsewhere on the site, and moves focus to
 * the server's success or error message after a post, so it is announced.
 */
import { getWilaya, subscribe } from '@theme/utils';

/** @typedef {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement} Control */

class BkReviewForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {(() => void) | null} */
  #unsubscribe = null;

  /** True while the area field still shows a value this element filled in (not the visitor's own pick). */
  #areaAutofilled = false;

  connectedCallback() {
    const form = this.querySelector('form');
    if (!(form instanceof HTMLFormElement)) return;

    this.#form = form;
    // JavaScript owns the messages from here on; without it the native rules still block a bad post.
    form.noValidate = true;
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    form.addEventListener('submit', (event) => this.#onSubmit(event), { signal });
    form.addEventListener('input', (event) => this.#clearFor(event.target), { signal });
    form.addEventListener('change', (event) => this.#clearFor(event.target), { signal });

    form.querySelector('[data-review-area]')?.addEventListener('change', () => { this.#areaAutofilled = false; }, { signal });
    // subscribe() hands the payload first: { wilaya }. An area chosen elsewhere on the page fills an untouched field.
    this.#unsubscribe = subscribe('bk:wilaya:change', (detail) => this.#prefillArea(detail?.wilaya ?? ''));

    this.#prefillArea(getWilaya());
    this.#focusStatus();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    this.#unsubscribe?.();
    this.#unsubscribe = null;
  }

  /**
   * Every validated field: its controls (several for the star radios), where its message goes and
   * the message itself.
   * @returns {{ controls: Control[], error: HTMLElement | null, message: string }[]}
   */
  #fields() {
    const form = this.#form;
    if (!form) return [];

    /** @type {{ controls: Control[], error: HTMLElement | null, message: string }[]} */
    const fields = [];
    for (const field of form.querySelectorAll('[data-review-field]')) {
      if (!(field instanceof HTMLElement)) continue;
      const controls = [...field.querySelectorAll('input, select, textarea')].filter(
        (element) =>
          element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement,
      );
      const error = field.querySelector('[data-review-error]');
      const key = field.dataset.reviewField ?? '';
      const message = this.dataset[`error${key.charAt(0).toUpperCase()}${key.slice(1)}`] ?? '';
      if (controls.length && message) {
        fields.push({
          controls: /** @type {Control[]} */ (controls),
          error: error instanceof HTMLElement ? error : null,
          message,
        });
      }
    }
    return fields;
  }

  /** @param {SubmitEvent} event */
  #onSubmit(event) {
    /** @type {Control | null} */
    let firstInvalid = null;

    for (const field of this.#fields()) {
      const valid = field.controls.every((control) => control.checkValidity());
      this.#setError(field, valid ? '' : field.message);
      if (!valid && !firstInvalid) firstInvalid = field.controls[0];
    }

    if (!firstInvalid) return;
    event.preventDefault();
    firstInvalid.focus();
  }

  /**
   * @param {{ controls: Control[], error: HTMLElement | null }} field
   * @param {string} message
   */
  #setError(field, message) {
    const { controls, error } = field;
    if (error) error.textContent = message;

    for (const control of controls) {
      if (message) {
        control.setAttribute('aria-invalid', 'true');
        if (error?.id) control.setAttribute('aria-describedby', error.id);
      } else {
        control.removeAttribute('aria-invalid');
        control.removeAttribute('aria-describedby');
      }
    }
  }

  /** @param {EventTarget | null} target */
  #clearFor(target) {
    if (!(target instanceof Element)) return;
    for (const field of this.#fields()) {
      if (field.controls.some((control) => control === target)) {
        this.#setError(field, '');
        return;
      }
    }
  }

  /** @param {string} area - Area name saved by the delivery checker */
  #prefillArea(area) {
    const select = this.#form?.querySelector('[data-review-area]');
    if (!(select instanceof HTMLSelectElement)) return;
    if (select.value && !this.#areaAutofilled) return;
    if (!area || ![...select.options].some((option) => option.value === area)) return;

    select.value = area;
    this.#areaAutofilled = true;
    this.#clearFor(select);
  }

  /** After a post, the server renders a success or error message: move focus there so it is read. */
  #focusStatus() {
    const status = this.querySelector('[data-review-status]');
    if (!(status instanceof HTMLElement)) return;
    requestAnimationFrame(() => {
      status.focus({ preventScroll: true });
      status.scrollIntoView({ block: 'center' });
    });
  }
}

if (!customElements.get('bk-review-form')) customElements.define('bk-review-form', BkReviewForm);
